import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useContext, useState } from "react";
import { useEffect } from "react";
import EnterpriseService from "../../common/services/enterprise-service";
import LoadingOverlay from "react-loading-overlay";
import { EnterpriseContext } from "./EnterpriseContext";
import Paginator from "../ui/Paginator";
import Tips from "../Tips";
import { useNavigate } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { confirmWrapper } from "../ui/askConfirm";
import { toast, ToastContainer } from "react-toastify";
import TransactionDetails from "./TransactionDetails";
import { Link } from "react-router-dom";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import truckyService from "../../common/services/trucky-service";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";
const classnames = require("classnames");

const Balance = ({ container }) => {
  const { globalContext } = useContext(EnterpriseContext);

  const company = globalContext.company;
  const member = globalContext.member;

  const es = new EnterpriseService();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 25 });
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const [showLoanDialog, toggleLoanDialog] = useState(false);
  const [availableLoans, setAvailableLoans] = useState([]);
  const [selectedLoanPlan, setSelectedLoanPlan] = useState(null);
  const [loans, setLoans] = useState([]);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);

  useEffect(() => {
    truckyService.setDocumentTitle("Company Balance");

    getBalanceStats();
    getCompanyLoans();
  }, []);

  const getTransactionDetails = async (id) => {
    const result = await es.getTransactionDetails(company, id);
    setTransactionDetails(result);
  };

  useEffect(() => {
    if (showLoanDialog && availableLoans.length == 0) {
      getAvailableLoans();
    }
  }, [showLoanDialog]);

  const getAvailableLoans = async () => {
    const result = await es.getAvailableLoans();
    setAvailableLoans(result);
  };

  const getCompanyLoans = async () => {
    if (es.can(member, "loans.manage")) {
      const result = await es.getCompanyLoans(company.id);
      if (!result.message) setLoans(result.loans);
    }
  };

  const getBalanceStats = async () => {
    const result = await es.getBalanceStats(company);
    if (!result.message) setStats(result);
  };

  useEffect(() => {
    getTransactions();
  }, [pagination]);

  const getTransactions = async () => {
    setLoading(true);
    const result = await es.getTransactions(company, pagination);
    setTransactions(result.data);
    setTotalPages(result.last_page);
    setLoading(false);
  };

  const handlePageClick = (event) => {
    setPagination({ ...pagination, page: event.selected + 1 });
  };

  const getTransactionDescription = (t) => {
    if (t.caused_by != null) {
      if (t.reason == "delivery") {
        return `${t.caused_by.from} - ${t.caused_by.to} (${t.caused_by.cargo})`;
      }

      if (t.reason == "garage") {
        return `${t.caused_by.name}`;
      }

      if (t.reason == "dealer") {
        return `${t.caused_by.name}`;
      }

      if (t.reason == "maintenance") {
        return `${t.caused_by.name}`;
      }
    } else {
      return "";
    }
  };

  const requestLoan = async () => {
    if (
      await confirmWrapper(
        container.localeManager.strings.areYouSureToRequestALoan,
        { container }
      )
    ) {
      const result = await es.requestLoan(company.id, selectedLoanPlan);
      if (result.success) {
        toggleLoanDialog(false);
        getTransactions();
        getCompanyLoans();
      } else {
        toast.error(result.message);
      }
    }
  };

  const repayLoan = async (id) => {
    const result = await es.repayLoan(company.id, id);
    getBalanceStats();
    getTransactions();
    getCompanyLoans();
  };

  const deleteTransaction = async (t) => {
    if (
      await confirmWrapper(container.localeManager.strings.areYouSure, {
        container,
      })
    ) {
      const result = await es.deleteTransaction(company, t.id);
      if (result.success) {
        getBalanceStats();
        getTransactions();
        toast.success(container.localeManager.strings.dataUpdated);
      } else {
        toast.error(result.message);
      }
    }
  };

  return (
    <LoadingOverlay
      active={loading}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/balance"} container={container}></VTCMenu>
        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        title: container.localeManager.strings.companyBalance,
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="page-hero mb-4">
                    <span className="page-hero-icon">
                      <FontAwesomeIcon icon="wallet" className="mx-0" />
                    </span>
                    <div className="flex-1">
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <h1 className="page-hero-title mb-0">
                          {container.localeManager.strings.companyBalance}
                        </h1>
                      </div>
                      {stats != null && (
                        <div className="page-hero-subtitle">
                          {container.localeManager.numeral(stats.total).format("0,0.[00]")} T¢ {container.localeManager.strings.total}
                        </div>
                      )}
                    </div>
                    {es.can(globalContext.member, "loans.manage") && (
                      <div className="ms-auto">
                        <Button
                          size="sm"
                          color="primary"
                          onClick={() => toggleLoanDialog(true)}
                        >
                          <FontAwesomeIcon icon="hand-holding-dollar" className="me-1" />
                          {container.localeManager.strings.requestLoan}
                        </Button>
                      </div>
                    )}
                  </div>
                  {stats != null && (
                    <Row className="g-3 mb-4">
                      <Col sm={4}>
                        <div className={`stat-tile stat-tile-with-icon stat-tile-${stats.total >= 0 ? 'success' : 'danger'}`}>
                          <div className="stat-tile-icon">
                            <FontAwesomeIcon icon="coins" className="mx-0" />
                          </div>
                          <div className="stat-tile-body">
                            <div className="counted fs-4 fw-bolder">
                              {container.localeManager.numeral(stats.total).format("0,0.[00]")} T¢
                            </div>
                            <div className="fw-bold fs-7 text-muted text-uppercase">
                              {container.localeManager.strings.total}
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col sm={4}>
                        <div className={`stat-tile stat-tile-with-icon stat-tile-${stats.today >= 0 ? 'info' : 'warning'}`}>
                          <div className="stat-tile-icon">
                            <FontAwesomeIcon icon="calendar-day" className="mx-0" />
                          </div>
                          <div className="stat-tile-body">
                            <div className="counted fs-4 fw-bolder">
                              {container.localeManager.numeral(stats.today).format("0,0.[00]")} T¢
                            </div>
                            <div className="fw-bold fs-7 text-muted text-uppercase">
                              {container.localeManager.strings.today}
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col sm={4}>
                        <div className={`stat-tile stat-tile-with-icon stat-tile-${stats.monthly >= 0 ? 'primary' : 'warning'}`}>
                          <div className="stat-tile-icon">
                            <FontAwesomeIcon icon="calendar-week" className="mx-0" />
                          </div>
                          <div className="stat-tile-body">
                            <div className="counted fs-4 fw-bolder">
                              {container.localeManager.numeral(stats.monthly).format("0,0.[00]")} T¢
                            </div>
                            <div className="fw-bold fs-7 text-muted text-uppercase">
                              {container.localeManager.strings.thisMonth}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                  {loans.length > 0 && (
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <span className="page-title-icon page-title-icon-danger page-title-icon-sm">
                          <FontAwesomeIcon icon="file-invoice-dollar" className="mx-0" />
                        </span>
                        <h6 className="mb-0 fw-bold">{container.localeManager.strings.currentLoans}</h6>
                      </div>
                      <Row className="g-3">
                        {loans.map((m) => (
                          <Col sm={6} lg={3} key={m.id}>
                            <div className="stat-tile stat-tile-danger h-100 p-3">
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <FontAwesomeIcon icon="hand-holding-dollar" className="text-danger mx-0" />
                                <span className="fw-bold fs-5 counted">
                                  {container.localeManager.numeral(m.requested_value).format("0,0.[00]")} T¢
                                </span>
                              </div>
                              <div className="detail-rows">
                                <div className="detail-row py-1">
                                  <span className="detail-row-label text-muted small">{container.localeManager.strings.remainingDays?.split('{0}')[0] || 'Remaining'}</span>
                                  <span className="detail-row-value small">{container.localeManager.numeral(m.remaining_value).format("0,0.[00]")} T¢</span>
                                </div>
                                <div className="detail-row py-1">
                                  <span className="detail-row-label text-muted small">{container.localeManager.strings.dailyInstalment?.split('{0}')[0] || 'Daily'}</span>
                                  <span className="detail-row-value small">{container.localeManager.numeral(m.instalment_value).format("0,0.[00]")} T¢</span>
                                </div>
                                <div className="detail-row py-1">
                                  <span className="detail-row-label text-muted small">{container.localeManager.strings.endsAt?.split('{0}')[0] || 'Ends'}</span>
                                  <span className="detail-row-value small">{container.localeManager.moment(m.pay_until).format("L")}</span>
                                </div>
                              </div>
                              {stats != null && stats.total > m.remaining_value && (
                                <Button color="success" size="sm" className="mt-2 w-100" onClick={() => repayLoan(m.id)}>
                                  <FontAwesomeIcon icon="check" className="me-1" />
                                  {container.localeManager.strings.repay}
                                </Button>
                              )}
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                  {!loading && transactions.length > 0 && (
                    <>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <span className="page-title-icon page-title-icon-primary page-title-icon-sm">
                            <FontAwesomeIcon icon="list" className="mx-0" />
                          </span>
                          <h6 className="mb-0 fw-bold">{container.localeManager.strings.transactions || 'Transactions'}</h6>
                        </div>
                        <Paginator
                          forcePage={pagination.page - 1}
                          totalPages={totalPages}
                          handlePageClick={handlePageClick}
                        />
                      </div>
                      <div className="table-wrap">
                      <Row>
                        <Col>
                          <Table
                            responsive
                            striped
                            size="sm"
                            className="table-row-dashed table-row-gray-300 align-middle gs-0 mt-3"
                            hover
                          >
                            <thead>
                              <tr className="fw-bolder text-gray-700 bg-light">
                                <th scope="col" className="ps-4">
                                  {container.localeManager.strings.date}
                                </th>
                                <th scope="col">
                                  {container.localeManager.strings.member}
                                </th>
                                <th scope="col">
                                  {container.localeManager.strings.reason}
                                </th>
                                <th scope="col">
                                  {container.localeManager.strings.details}
                                </th>
                                <th scope="col" className="pe-4">
                                  {container.localeManager.strings.value}
                                </th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {transactions.map((m) => {
                                return (
                                  <tr>
                                    <td className="ps-2">
                                      {container.localeManager
                                        .moment(m.created_at)
                                        .format("l")}
                                    </td>
                                    <td>
                                      {m.user != null ? (
                                        <Link to={`/user/${m.user.id}`}>
                                          {m.user.name}
                                        </Link>
                                      ) : (
                                        ""
                                      )}
                                    </td>
                                    <td>
                                      {
                                        container.localeManager.strings[
                                          m.reason
                                        ]
                                      }
                                      {m.reason == "salaries" &&
                                        m.salaries_details_count > 0 && (
                                          <a
                                            className="ms-3 text-white"
                                            onClick={() =>
                                              getTransactionDetails(m.id)
                                            }
                                          >
                                            <FontAwesomeIcon icon="info-circle"></FontAwesomeIcon>
                                          </a>
                                        )}
                                    </td>
                                    <td>{getTransactionDescription(m)}</td>
                                    <td
                                      className={classnames({
                                        "text-success": m.value > 0,
                                        "text-danger": m.value <= 0,
                                      })}
                                    >
                                      {container.localeManager
                                        .numeral(m.value)
                                        .format("0,0.[00]")}{" "}
                                      {m.currency}
                                    </td>
                                    <td>
                                      {m.reason == "salaries" &&
                                        stats != null && stats.total < 0 &&
                                        es.can(member, "company.*") && (
                                          <Button
                                            color="link"
                                            className="p-0"
                                            onClick={() => deleteTransaction(m)}
                                          >
                                            <FontAwesomeIcon
                                              icon="trash"
                                              className="text-danger m-0"
                                            ></FontAwesomeIcon>
                                          </Button>
                                        )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
            <aside className="ad-column-placeholder">
              <Tips localeManager={container.localeManager}></Tips>
              {!window.overwolf && (
                <BoxDynamicAd container={container} className="my-3" />
              )}
            </aside>
          </Row>
        </Col>
      </Row>
      <Modal
        size="lg"
        isOpen={showLoanDialog}
        toggle={() => toggleLoanDialog(false)}
      >
        <ModalHeader
          toggle={() => toggleLoanDialog(false)}
          close={
            <Button
              color="active-light-primary"
              className="btn-icon btn-sm w-30px h-30px ms-auto"
              onClick={() => toggleLoanDialog(false)}
            >
              <FontAwesomeIcon icon="times"></FontAwesomeIcon>
            </Button>
          }
        >
          {container.localeManager.strings.requestLoan}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <FontAwesomeIcon icon="info-circle"></FontAwesomeIcon>
            {container.localeManager.strings.loanInfo}
          </div>
          <div className="grid cols-2">
            {availableLoans.map((m) => (
              <div
                key={m.id}
                className={classnames("stat-tile stat-tile-with-icon", {
                  "stat-tile-success": selectedLoanPlan != null && selectedLoanPlan.id === m.id,
                  "stat-tile-primary": selectedLoanPlan == null || selectedLoanPlan.id !== m.id,
                })}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedLoanPlan(m)}
              >
                <div className="stat-tile-icon">
                  <FontAwesomeIcon icon="hand-holding-dollar" className="mx-0" />
                </div>
                <div className="stat-tile-body">
                  <div className="counted fs-4 fw-bolder">
                    {container.localeManager.numeral(m.value).format("0,0.[00]")} T¢
                  </div>
                  <div className="detail-rows mt-2">
                    <div className="detail-row py-1">
                      <span className="text-muted small">
                        {container.localeManager.strings.formatString(container.localeManager.strings.duratiooin, m.duration)}
                      </span>
                    </div>
                    <div className="detail-row py-1">
                      <span className="text-muted small">
                        {container.localeManager.strings.formatString(container.localeManager.strings.rate, m.rate)}
                      </span>
                    </div>
                    <div className="detail-row py-1">
                      <span className="text-muted small">
                        {container.localeManager.strings.formatString(container.localeManager.strings.dailyInstalment, container.localeManager.numeral(m.instalment_value).format("0,0.[00]"))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {selectedLoanPlan != null && (
            <div className="text-end mt-3">
              <Button onClick={requestLoan} color="primary">
                {container.localeManager.strings.requestLoan}
              </Button>
            </div>
          )}
        </ModalBody>
      </Modal>
      {transactionDetails != null && (
        <TransactionDetails
          company={company}
          container={container}
          details={transactionDetails}
          onClose={() => setTransactionDetails(null)}
        ></TransactionDetails>
      )}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </LoadingOverlay>
  );
};

export default Balance;
